import { getProtocols, ApiProtocol, postProtocol, ApiProtocolError, putProtocol, Unsaved } from "../utils/api";
import { useCallback, useEffect, useState } from "react";
import { AxiosError } from "axios";
import { showNotification } from "utils/notifications";
import _ from "lodash";

interface UseProtocolsResult {
  /** A sorted array of ApiProtocol's. They are sorted by name.  */
  protocols: ApiProtocol[] | null;
  /** A boolean indicator for when the results of a ReST call are being waiting on. */
  loading: boolean;
  /** An object of protocol id's to strings that represent an error returned while trying to save or add a protocol. Used to show errors in forms.  */
  fieldErrors: ApiProtocolError;
  /** A string message from a general API error. */
  error: string | null;
  /** A function to add new protocols in the backend. */
  addProtocol(protocol: Unsaved<ApiProtocol>): Promise<ApiProtocol>;
  /** A function to update existing protocols in the backend. */
  updateProtocol(protocol: ApiProtocol): Promise<ApiProtocol>;
}

/** A hook for interacting with test protocols. */
export function useProtocols(): UseProtocolsResult {
  const [protocols, setProtocols] = useState<Array<ApiProtocol> | null>(null);
  const [loading, setLoading] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<ApiProtocolError>({});
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    const signal = new AbortController().signal;
    getProtocols(signal)
      .then((protocols) => {
        setLoading(false);
        setProtocols(protocols);
      })
      .catch((err) => {
        setLoading(false);
        console.error("Failed to fetch protocols:", err);
        setError(err.toString());
      });
  }, []);

  const addProtocolToState = useCallback(
    (newProtocol: ApiProtocol) => {
      if (protocols) {
        const filteredProtocols = protocols.filter((protocol) => {
          return protocol.id !== newProtocol.id;
        });
        setProtocols(_.sortBy([...filteredProtocols, newProtocol], (proto) => proto.name.toLowerCase()));
      } else {
        throw new Error("Stopped an attempt to add a protocol before existing protocol have been fetched.");
      }
    },
    [protocols]
  );

  const onAddUpdateSuccess = useCallback(
    (savedProtocol: ApiProtocol) => {
      setLoading(false);
      setFieldErrors({});
      addProtocolToState(savedProtocol);

      showNotification({
        message: "Protocol saved successfully.",
        severity: "success",
      });

      return savedProtocol;
    },
    [setLoading, setFieldErrors, addProtocolToState]
  );

  const onAddUpdateError = useCallback((err: unknown) => {
    //@ts-expect-error ignore type
    if (err.name === "AbortError") {
      console.log("Fetch aborted: previous request cancelled or component unmounted");
    } else {
      setLoading(false);
      if ((err as AxiosError).response?.status === 400 && (err as AxiosError).response?.data) {
        // setFieldErrors((err as AxiosError<API).response.data);
        showNotification({
          message: "Failed to save Protocol, fix errors and try again.",
          severity: "error",
        });
      } else {
        showNotification({
          message: "Failed to save Protocol, please reload the page and try again.",
          severity: "error",
        });
      }
    }
    throw err;
  }, []);

  const addProtocol = useCallback(
    (protocol: Unsaved<ApiProtocol>) => {
      const signal = new AbortController().signal;
      setLoading(true);
      return postProtocol(protocol, signal).then(onAddUpdateSuccess).catch(onAddUpdateError);
    },
    [onAddUpdateSuccess, onAddUpdateError]
  );

  const updateProtocol = useCallback(
    (protocol: ApiProtocol) => {
      const signal = new AbortController().signal;
      setLoading(true);
      return putProtocol(protocol, signal).then(onAddUpdateSuccess).catch(onAddUpdateError);
    },
    [onAddUpdateSuccess, onAddUpdateError]
  );

  return { protocols, loading, fieldErrors, error, addProtocol, updateProtocol };
}
